import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { queryKeys } from '../../lib/queryKeys';
import { Badge, Button, Card, Checkbox, EmptyState, Field, HeroSelectField, Input, LoadingScreen, Modal, PageHeader, Switch, Textarea } from '../components/Ui';
import { useToast } from '../contexts/ToastContext';
import { useBusinessMenuData, useBulkUpdateProducts, useCreateOptionGroup, useUpdateOptionGroup, useDeactivateOptionGroup, useGenerateProductAiMetadata, useReorderCategories, useReorderProducts } from '../hooks/businessData';
import { apiRequest, jsonBody } from '../lib/api';
import { formatUsd } from '../lib/currency';
import { uploadImage } from '../lib/upload';
import type { MenuAiMetadata, MenuAiRole } from '../../shared/types';

type ProductType = 'simple' | 'combo';
type PriceMode = 'fijo' | 'variante';
type SaleUnit = 'unidad' | 'porcion' | 'otra';
type PromoType = 'none' | 'percentage' | 'fixed';
type SelectionType = 'single' | 'multiple' | 'quantity';
type BranchState = 'disponible' | 'agotado' | 'desactivado';
type SaleStyle = 'simple' | 'variants' | 'configurable' | 'combo';

interface Category { id: string; name: string; description: string; imageKey: string | null; imageUrl: string | null; order: number; active: boolean; aiMetadata: MenuAiMetadata }
interface Branch { id: string; name: string; active: boolean }
interface Ingredient {
  id?: string; name: string; includedByDefault: boolean; selectable: boolean;
  extraPriceUsdCents: number; active: boolean; order: number;
}
interface ProductVariant {
  id?: string; name: string; sku: string | null; priceUsdCents: number; active: boolean; order: number;
}
interface Option {
  id?: string; name: string; extraPriceUsdCents: number; active: boolean; order: number;
}
interface OptionGroup {
  id: string; name: string; description: string; selectionType: SelectionType;
  minSelections: number; maxSelections: number; required: boolean; reusable: boolean;
  active: boolean; order: number; options: Option[];
}
interface OptionGroupAssignment {
  id?: string; groupId: string; order: number; requiredOverride: boolean | null;
  minSelectionsOverride: number | null; maxSelectionsOverride: number | null; variantIds: string[];
}
interface ProductAvailability { branchId: string; state: BranchState; preparationZone: string | null }
interface ComboChoice {
  id?: string; productId: string; extraPriceUsdCents: number; default: boolean; active: boolean; order: number;
}
interface ComboComponent {
  id?: string; name: string; selectionType: 'fijo' | 'eleccion'; quantity: number;
  active: boolean; order: number; products: ComboChoice[];
}
interface Product {
  id: string; categoryId: string | null; name: string; description: string; priceUsdCents: number;
  promoType: PromoType; promoValue: number; imageKeys: string[]; imageUrls: string[];
  preparationZone: string; preparationTimeMinutes: number | null; active: boolean; order: number; ingredients: Ingredient[];
  type: ProductType; priceMode: PriceMode; saleUnit: SaleUnit; saleUnitName: string | null;
  variants: ProductVariant[]; optionGroups: OptionGroupAssignment[];
  availability: ProductAvailability[]; comboComponents: ComboComponent[];
  aiMetadata: MenuAiMetadata;
}

interface IngredientForm extends Omit<Ingredient, 'extraPriceUsdCents' | 'order'> { extraPriceUsd: string; order: string }
interface VariantForm extends Omit<ProductVariant, 'priceUsdCents' | 'order'> { priceUsd: string; order: string }
interface AssignmentForm extends Omit<OptionGroupAssignment, 'order' | 'requiredOverride' | 'minSelectionsOverride' | 'maxSelectionsOverride'> {
  order: string; requiredOverride: 'inherit' | 'true' | 'false'; minSelectionsOverride: string; maxSelectionsOverride: string;
}
interface AvailabilityForm { branchId: string; state: BranchState; preparationZone: string }
interface ComboChoiceForm extends Omit<ComboChoice, 'extraPriceUsdCents' | 'order'> { extraPriceUsd: string; order: string }
interface ComboComponentForm extends Omit<ComboComponent, 'quantity' | 'order' | 'products'> {
  quantity: string; order: string; products: ComboChoiceForm[];
}
interface ProductForm {
  name: string; description: string; categoryId: string; priceUsd: string; promoType: PromoType;
  promoValue: string; imageKeys: string[]; imageUrls: string[]; preparationZone: string; preparationTimeMinutes: string;
  active: boolean; order: string; type: ProductType; priceMode: PriceMode; saleUnit: SaleUnit;
  saleUnitName: string; ingredients: IngredientForm[]; variants: VariantForm[];
  optionGroups: AssignmentForm[]; availability: AvailabilityForm[]; comboComponents: ComboComponentForm[];
  aiMetadata: AiMetadataForm;
}
interface AiMetadataForm {
  aiAliases: string;
  aiTags: string;
  aiRole: MenuAiRole;
  aiPriority: string;
  showInAiOverview: boolean;
  aiDescription: string;
}
type MenuProductIssue = { label: string; tone: 'slate' | 'amber' | 'red' };
type BulkActiveAction = 'keep' | 'activate' | 'deactivate';
interface OptionForm extends Omit<Option, 'extraPriceUsdCents' | 'order'> { extraPriceUsd: string; order: string }
interface OptionGroupForm {
  name: string; description: string; selectionType: SelectionType; minSelections: string;
  maxSelections: string; required: boolean; options: OptionForm[];
}

const saleStyles: Array<{ id: SaleStyle; title: string; description: string; example: string }> = [
  { id: 'simple', title: 'Simple', description: 'Un producto, un precio.', example: 'Café, postre, bebida' },
  { id: 'variants', title: 'Tamaños o presentaciones', description: 'Cada variante tiene nombre, SKU y precio.', example: 'Pequeña, mediana, grande' },
  { id: 'configurable', title: 'Configurable', description: 'El cliente elige opciones y extras.', example: 'Pizza con ingredientes' },
  { id: 'combo', title: 'Combo', description: 'Incluye productos fijos o a elección.', example: 'Menú con bebida y acompañante' },
];

const emptyGroupForm = (): OptionGroupForm => ({
  name: '', description: '', selectionType: 'single', minSelections: '0', maxSelections: '1',
  required: false, options: [{ name: '', extraPriceUsd: '0', active: true, order: '0' }],
});

const defaultAiMetadata: MenuAiMetadata = {
  aiAliases: [],
  aiTags: [],
  aiRole: 'unknown',
  aiPriority: 3,
  showInAiOverview: true,
  aiDescription: '',
};

const aiRoleOptions: Array<{ id: MenuAiRole; label: string }> = [
  { id: 'unknown', label: 'Sin clasificar' },
  { id: 'primary', label: 'Principal' },
  { id: 'side', label: 'Acompañante' },
  { id: 'beverage', label: 'Bebida' },
  { id: 'dessert', label: 'Postre' },
  { id: 'addon', label: 'Extra / adicional' },
];

const metadataToForm = (metadata?: MenuAiMetadata): AiMetadataForm => {
  const value = metadata ?? defaultAiMetadata;
  return {
    aiAliases: value.aiAliases.join(', '),
    aiTags: value.aiTags.join(', '),
    aiRole: value.aiRole,
    aiPriority: String(value.aiPriority),
    showInAiOverview: value.showInAiOverview,
    aiDescription: value.aiDescription,
  };
};

const splitAiTerms = (value: string): string[] => [...new Set(value.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean))].slice(0, 12);

const metadataFromForm = (form: AiMetadataForm): MenuAiMetadata => ({
  aiAliases: splitAiTerms(form.aiAliases).map((item) => item.slice(0, 40)),
  aiTags: splitAiTerms(form.aiTags).map((item) => item.slice(0, 40)),
  aiRole: form.aiRole,
  aiPriority: Math.min(5, Math.max(0, Number(form.aiPriority || 3))),
  showInAiOverview: form.showInAiOverview,
  aiDescription: form.aiDescription.trim().slice(0, 300),
});

function hasPoorAiMetadata(metadata: MenuAiMetadata, product: Product): boolean {
  const usefulTerms = new Set([...metadata.aiAliases, ...metadata.aiTags].map((item) => item.trim()).filter(Boolean));
  const needsDescription = product.description.trim().length > 0 && metadata.aiDescription.trim().length < 12;
  return metadata.aiRole === 'unknown'
    || metadata.aiAliases.length < 1
    || metadata.aiTags.length < 2
    || usefulTerms.size < 3
    || needsDescription;
}

function getMenuProductIssues(product: Product, categories: Category[], products: Product[]): MenuProductIssue[] {
  const issues: MenuProductIssue[] = [];
  const category = product.categoryId ? categories.find((item) => item.id === product.categoryId) : null;

  if (!product.active) issues.push({ label: 'Inactivo', tone: 'slate' });
  if (product.imageKeys.length === 0 && product.imageUrls.length === 0) issues.push({ label: 'Sin imagen', tone: 'amber' });
  if (!product.categoryId) issues.push({ label: 'Sin categoría', tone: 'amber' });
  else if (!category) issues.push({ label: 'Categoría inválida', tone: 'red' });
  else if (!category.active) issues.push({ label: 'Categoría inactiva', tone: 'amber' });

  if (product.priceMode === 'fijo' && product.priceUsdCents <= 0) issues.push({ label: 'Sin precio', tone: 'amber' });
  if (product.priceMode === 'variante' && !product.variants.some((variant) => variant.active && variant.priceUsdCents > 0)) {
    issues.push({ label: 'Sin precio de variante', tone: 'amber' });
  }
  if (!product.preparationZone.trim()) issues.push({ label: 'Sin zona', tone: 'amber' });

  if (product.type === 'combo') {
    const activeComponents = product.comboComponents.filter((component) => component.active);
    if (activeComponents.length === 0) {
      issues.push({ label: 'Combo sin componentes', tone: 'amber' });
    } else {
      const activeProductIds = new Set(products.filter((item) => item.active).map((item) => item.id));
      const hasIncompleteComponent = activeComponents.some((component) => (
        component.quantity <= 0
        || !component.products.some((choice) => choice.active && activeProductIds.has(choice.productId))
      ));
      if (hasIncompleteComponent) issues.push({ label: 'Combo incompleto', tone: 'amber' });
    }
  }

  const metadata = product.aiMetadata ?? defaultAiMetadata;
  if (hasPoorAiMetadata(metadata, product)) {
    issues.push({ label: 'IA incompleta', tone: 'amber' });
  }

  return issues;
}

const emptyProduct = (branches: Branch[]): ProductForm => ({
  name: '', description: '', categoryId: '', priceUsd: '', promoType: 'none', promoValue: '0',
  imageKeys: [], imageUrls: [], preparationZone: 'general', preparationTimeMinutes: '', active: true, order: '0',
  type: 'simple', priceMode: 'fijo', saleUnit: 'unidad', saleUnitName: '', ingredients: [],
  variants: [], optionGroups: [],
  availability: branches.map((branch) => ({ branchId: branch.id, state: 'disponible', preparationZone: '' })),
  comboComponents: [],
  aiMetadata: metadataToForm(),
});

function saleStyleFor(product: Product): SaleStyle {
  if (product.type === 'combo') return 'combo';
  if (product.priceMode === 'variante') return 'variants';
  if (product.optionGroups.length) return 'configurable';
  return 'simple';
}

function EditorSection({ title, description, children, open = false }: { title: string; description: string; children: ReactNode; open?: boolean }) {
  return (
    <details open={open} className="group rounded-2xl border border-slate-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 sm:p-5">
        <div><h3 className="font-black text-slate-950">{title}</h3><p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p></div>
        <span className="text-xl font-light text-slate-400 transition group-open:rotate-45">+</span>
      </summary>
      <div className="border-t border-slate-100 p-4 sm:p-5">{children}</div>
    </details>
  );
}

export default function MenuManagementPage() {
  const { show } = useToast();
  const queryClient = useQueryClient();
  const { data: menuData, isLoading: loading, isError, error } = useBusinessMenuData();
  const categories = (menuData?.categories ?? []) as unknown as Category[];
  const products = (menuData?.products ?? []) as unknown as Product[];
  const optionGroups = (menuData?.optionGroups ?? []) as unknown as OptionGroup[];
  const branches = (menuData?.branches ?? []) as unknown as Branch[];
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [onlyMenuProblems, setOnlyMenuProblems] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [saleStyle, setSaleStyle] = useState<SaleStyle | null>(null);
  const [productForm, setProductForm] = useState<ProductForm>(() => emptyProduct([]));
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', imageKey: null as string | null, imageUrl: null as string | null, order: '0', active: true, aiMetadata: metadataToForm() });
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState<OptionGroupForm>(emptyGroupForm);
  const [busy, setBusy] = useState(false);
  const [groupBusy, setGroupBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [groupLibraryOpen, setGroupLibraryOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [groupLibraryForm, setGroupLibraryForm] = useState<OptionGroupForm>(emptyGroupForm());
  const [ingredientCatalogKey, setIngredientCatalogKey] = useState('');
  const [groupAssignKey, setGroupAssignKey] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(() => new Set());
  const [bulkActiveAction, setBulkActiveAction] = useState<BulkActiveAction>('keep');
  const [bulkCategoryId, setBulkCategoryId] = useState('__keep__');
  const [bulkPreparationZone, setBulkPreparationZone] = useState('');

  const bulkUpdateProducts = useBulkUpdateProducts();
  const reorderCategories = useReorderCategories();
  const reorderProducts = useReorderProducts();
  const generateProductAiMetadata = useGenerateProductAiMetadata();
  const createOptionGroupMutation = useCreateOptionGroup();
  const updateOptionGroupMutation = useUpdateOptionGroup();
  const deactivateOptionGroupMutation = useDeactivateOptionGroup();

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudo cargar el menú.', 'error');
    }
  }, [error, isError, show]);

  const productIssuesById = useMemo(() => new Map(products.map((product) => [
    product.id,
    getMenuProductIssues(product, categories, products),
  ])), [products, categories]);
  const productsWithIssuesCount = useMemo(() => [...productIssuesById.values()].filter((issues) => issues.length > 0).length, [productIssuesById]);
  const filtered = useMemo(() => products.filter((product) => {
    const matchesSearch = !search || product.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || product.categoryId === categoryFilter;
    const matchesProblems = !onlyMenuProblems || (productIssuesById.get(product.id)?.length ?? 0) > 0;
    return matchesSearch && matchesCategory && matchesProblems;
  }), [products, search, categoryFilter, onlyMenuProblems, productIssuesById]);
  const simpleProducts = useMemo(() => products.filter((product) => product.type === 'simple' && product.id !== editingProductId), [products, editingProductId]);
  const ingredientCatalogNames = useMemo(() => [...new Set(
    products.flatMap((product) => product.ingredients.map((ingredient) => ingredient.name.trim()).filter(Boolean)),
  )], [products]);
  const selectedCount = selectedProductIds.size;
  const allVisibleSelected = filtered.length > 0 && filtered.every((product) => selectedProductIds.has(product.id));

  const toggleProductSelection = (productId: string, selected: boolean) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (selected) next.add(productId);
      else next.delete(productId);
      return next;
    });
  };

  const toggleVisibleSelection = (selected: boolean) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      for (const product of filtered) {
        if (selected) next.add(product.id);
        else next.delete(product.id);
      }
      return next;
    });
  };

  const clearBulkSelection = () => {
    setSelectedProductIds(new Set());
    setBulkActiveAction('keep');
    setBulkCategoryId('__keep__');
    setBulkPreparationZone('');
  };

  const applyBulkChanges = async () => {
    const payload: { productIds: string[]; active?: boolean; categoryId?: string | null; preparationZone?: string } = {
      productIds: [...selectedProductIds],
    };
    if (bulkActiveAction !== 'keep') payload.active = bulkActiveAction === 'activate';
    if (bulkCategoryId !== '__keep__') payload.categoryId = bulkCategoryId === '__none__' ? null : bulkCategoryId;
    const trimmedZone = bulkPreparationZone.trim();
    if (trimmedZone) payload.preparationZone = trimmedZone;
    if (payload.active === undefined && payload.categoryId === undefined && payload.preparationZone === undefined) {
      show('Elegí al menos un cambio para aplicar.', 'error');
      return;
    }
    try {
      const result = await bulkUpdateProducts.mutateAsync(payload);
      show(`${result.updated} producto(s) actualizados.`, 'success');
      clearBulkSelection();
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo aplicar la edición masiva.', 'error');
    }
  };

  const reorderCategory = async (categoryId: string, direction: -1 | 1) => {
    const currentIndex = categories.findIndex((category) => category.id === categoryId);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= categories.length) return;
    const ordered = [...categories];
    const current = ordered[currentIndex];
    const target = ordered[targetIndex];
    if (!current || !target) return;
    ordered[currentIndex] = target;
    ordered[targetIndex] = current;
    try {
      await reorderCategories.mutateAsync({ items: ordered.map((category, index) => ({ id: category.id, order: index })) });
      show('Orden de categorías actualizado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar el orden de categorías.', 'error');
    }
  };

  const reorderVisibleProduct = async (productId: string, direction: -1 | 1) => {
    const currentIndex = filtered.findIndex((product) => product.id === productId);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= filtered.length) return;
    const ordered = [...filtered];
    const current = ordered[currentIndex];
    const target = ordered[targetIndex];
    if (!current || !target) return;
    ordered[currentIndex] = target;
    ordered[targetIndex] = current;
    try {
      await reorderProducts.mutateAsync({ items: ordered.map((product, index) => ({ id: product.id, order: index })) });
      show('Orden de productos actualizado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar el orden de productos.', 'error');
    }
  };

  const generateSelectedAiMetadata = async () => {
    try {
      const result = await generateProductAiMetadata.mutateAsync({ productIds: [...selectedProductIds] });
      show(`${result.updated} sugerencia(s) determinísticas guardadas.`, 'success');
      clearBulkSelection();
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudieron generar las sugerencias.', 'error');
    }
  };

  const newProduct = () => {
    setEditingProductId(null);
    setProductForm(emptyProduct(branches));
    setSaleStyle(null);
    setNewGroupOpen(false);
    setProductOpen(true);
  };

  const editProduct = (product: Product) => {
    const byBranch = new Map(product.availability.map((item) => [item.branchId, item]));
    setEditingProductId(product.id);
    setSaleStyle(saleStyleFor(product));
    setNewGroupOpen(false);
    setProductForm({
      name: product.name,
      description: product.description,
      categoryId: product.categoryId || '',
      priceUsd: (product.priceUsdCents / 100).toFixed(2),
      promoType: product.promoType,
      promoValue: product.promoType === 'fixed' ? (product.promoValue / 100).toFixed(2) : String(product.promoValue),
      imageKeys: product.imageKeys,
      imageUrls: product.imageUrls,
      preparationZone: product.preparationZone,
      preparationTimeMinutes: product.preparationTimeMinutes == null ? '' : String(product.preparationTimeMinutes),
      active: product.active,
      order: String(product.order),
      type: product.type,
      priceMode: product.priceMode,
      saleUnit: product.saleUnit,
      saleUnitName: product.saleUnitName ?? '',
      ingredients: product.ingredients.map((ingredient) => ({
        ...ingredient, extraPriceUsd: (ingredient.extraPriceUsdCents / 100).toFixed(2), order: String(ingredient.order),
      })),
      variants: product.variants.map((variant) => ({
        ...variant, priceUsd: (variant.priceUsdCents / 100).toFixed(2), order: String(variant.order),
      })),
      optionGroups: product.optionGroups.map((assignment) => ({
        id: assignment.id,
        groupId: assignment.groupId,
        order: String(assignment.order),
        requiredOverride: assignment.requiredOverride === null ? 'inherit' : String(assignment.requiredOverride) as 'true' | 'false',
        minSelectionsOverride: assignment.minSelectionsOverride === null ? '' : String(assignment.minSelectionsOverride),
        maxSelectionsOverride: assignment.maxSelectionsOverride === null ? '' : String(assignment.maxSelectionsOverride),
        variantIds: assignment.variantIds,
      })),
      availability: branches.map((branch) => {
        const availability = byBranch.get(branch.id);
        return { branchId: branch.id, state: availability?.state ?? 'disponible', preparationZone: availability?.preparationZone ?? '' };
      }),
      comboComponents: product.comboComponents.map((component) => ({
        ...component,
        quantity: String(component.quantity),
        order: String(component.order),
        products: component.products.map((choice) => ({
          ...choice, extraPriceUsd: (choice.extraPriceUsdCents / 100).toFixed(2), order: String(choice.order),
        })),
      })),
      aiMetadata: metadataToForm(product.aiMetadata),
    });
    setProductOpen(true);
  };

  const duplicateProduct = (product: Product) => {
    const byBranch = new Map(product.availability.map((item) => [item.branchId, item]));
    const variantIdMap = new Map<string, string>();
    const variants = product.variants.map((variant) => {
      const id = crypto.randomUUID();
      if (variant.id) variantIdMap.set(variant.id, id);
      return {
        id,
        name: variant.name,
        sku: '',
        priceUsd: (variant.priceUsdCents / 100).toFixed(2),
        active: variant.active,
        order: String(variant.order),
      };
    });

    setEditingProductId(null);
    setSaleStyle(saleStyleFor(product));
    setNewGroupOpen(false);
    setProductForm({
      name: `Copia de ${product.name}`,
      description: product.description,
      categoryId: product.categoryId || '',
      priceUsd: (product.priceUsdCents / 100).toFixed(2),
      promoType: product.promoType,
      promoValue: product.promoType === 'fixed' ? (product.promoValue / 100).toFixed(2) : String(product.promoValue),
      imageKeys: [...product.imageKeys],
      imageUrls: [...product.imageUrls],
      preparationZone: product.preparationZone,
      preparationTimeMinutes: product.preparationTimeMinutes == null ? '' : String(product.preparationTimeMinutes),
      active: false,
      order: String(product.order),
      type: product.type,
      priceMode: product.priceMode,
      saleUnit: product.saleUnit,
      saleUnitName: product.saleUnitName ?? '',
      ingredients: product.ingredients.map((ingredient) => ({
        name: ingredient.name,
        includedByDefault: ingredient.includedByDefault,
        selectable: ingredient.selectable,
        extraPriceUsd: (ingredient.extraPriceUsdCents / 100).toFixed(2),
        active: ingredient.active,
        order: String(ingredient.order),
      })),
      variants,
      optionGroups: product.optionGroups.map((assignment) => ({
        groupId: assignment.groupId,
        order: String(assignment.order),
        requiredOverride: assignment.requiredOverride === null ? 'inherit' : String(assignment.requiredOverride) as 'true' | 'false',
        minSelectionsOverride: assignment.minSelectionsOverride === null ? '' : String(assignment.minSelectionsOverride),
        maxSelectionsOverride: assignment.maxSelectionsOverride === null ? '' : String(assignment.maxSelectionsOverride),
        variantIds: assignment.variantIds.map((variantId) => variantIdMap.get(variantId)).filter((variantId): variantId is string => Boolean(variantId)),
      })),
      availability: branches.map((branch) => {
        const availability = byBranch.get(branch.id);
        return { branchId: branch.id, state: availability?.state ?? 'disponible', preparationZone: availability?.preparationZone ?? '' };
      }),
      comboComponents: product.comboComponents.map((component) => ({
        name: component.name,
        selectionType: component.selectionType,
        quantity: String(component.quantity),
        active: component.active,
        order: String(component.order),
        products: component.products.map((choice) => ({
          productId: choice.productId,
          extraPriceUsd: (choice.extraPriceUsdCents / 100).toFixed(2),
          default: choice.default,
          active: choice.active,
          order: String(choice.order),
        })),
      })),
      aiMetadata: metadataToForm(product.aiMetadata),
    });
    setProductOpen(true);
    show('Producto copiado. Revisá y guardá la copia.', 'success');
  };

  const applySaleStyle = (style: SaleStyle) => {
    setSaleStyle(style);
    setProductForm((current) => ({
      ...current,
      type: style === 'combo' ? 'combo' : 'simple',
      priceMode: style === 'variants' ? 'variante' : 'fijo',
      promoType: style === 'variants' && current.promoType === 'fixed' ? 'none' : current.promoType,
      promoValue: style === 'variants' && current.promoType === 'fixed' ? '0' : current.promoValue,
      variants: style === 'variants' ? current.variants : [],
      optionGroups: style === 'configurable' || style === 'variants'
        ? current.optionGroups.map((assignment) => ({ ...assignment, variantIds: style === 'variants' ? assignment.variantIds : [] }))
        : [],
      comboComponents: style === 'combo' ? current.comboComponents : [],
    }));
  };

  const newCategory = () => {
    setEditingCategoryId(null);
    setCategoryForm({ name: '', description: '', imageKey: null, imageUrl: null, order: '0', active: true, aiMetadata: metadataToForm() });
    setCategoryOpen(true);
  };
  const editCategory = (category: Category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({ name: category.name, description: category.description, imageKey: category.imageKey, imageUrl: category.imageUrl, order: String(category.order), active: category.active, aiMetadata: metadataToForm(category.aiMetadata) });
    setCategoryOpen(true);
  };

  const saveCategory = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await apiRequest(editingCategoryId ? `/api/business/categories/${editingCategoryId}` : '/api/business/categories', {
        method: editingCategoryId ? 'PATCH' : 'POST',
        body: jsonBody({ name: categoryForm.name, description: categoryForm.description, imageKey: categoryForm.imageKey, order: Number(categoryForm.order), active: categoryForm.active, aiMetadata: metadataFromForm(categoryForm.aiMetadata) }),
      });
      setCategoryOpen(false);
      show(editingCategoryId ? 'Categoría actualizada.' : 'Categoría creada.', 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.categories }),
      ]);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo guardar la categoría.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveProduct = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const priceUsdCents = Math.round(Number(productForm.priceUsd) * 100);
      const promoValue = productForm.promoType === 'fixed' ? Math.round(Number(productForm.promoValue) * 100) : Number(productForm.promoValue);
      const payload = {
        categoryId: productForm.categoryId || null,
        name: productForm.name,
        description: productForm.description,
        priceUsdCents,
        promoType: productForm.promoType,
        promoValue: productForm.promoType === 'none' ? 0 : promoValue,
        imageKeys: productForm.imageKeys,
        preparationZone: productForm.preparationZone,
        preparationTimeMinutes: productForm.preparationTimeMinutes.trim() === '' ? null : Number(productForm.preparationTimeMinutes),
        active: productForm.active,
        order: Number(productForm.order),
        type: productForm.type,
        priceMode: productForm.priceMode,
        saleUnit: productForm.saleUnit,
        saleUnitName: productForm.saleUnit === 'otra' ? productForm.saleUnitName : null,
        aiMetadata: metadataFromForm(productForm.aiMetadata),
        ingredients: productForm.ingredients.map((ingredient) => ({
          id: ingredient.id,
          name: ingredient.name,
          includedByDefault: ingredient.includedByDefault,
          selectable: ingredient.selectable,
          extraPriceUsdCents: Math.round(Number(ingredient.extraPriceUsd || 0) * 100),
          active: ingredient.active,
          order: Number(ingredient.order),
        })),
        variants: productForm.variants.map((variant) => ({
          id: variant.id,
          name: variant.name,
          sku: variant.sku?.trim() || null,
          priceUsdCents: Math.round(Number(variant.priceUsd) * 100),
          active: variant.active,
          order: Number(variant.order),
        })),
        optionGroups: productForm.optionGroups.map((assignment) => ({
          id: assignment.id,
          groupId: assignment.groupId,
          order: Number(assignment.order),
          requiredOverride: assignment.requiredOverride === 'inherit' ? null : assignment.requiredOverride === 'true',
          minSelectionsOverride: assignment.minSelectionsOverride === '' ? null : Number(assignment.minSelectionsOverride),
          maxSelectionsOverride: assignment.maxSelectionsOverride === '' ? null : Number(assignment.maxSelectionsOverride),
          variantIds: assignment.variantIds,
        })),
        availability: productForm.availability.map((item) => ({
          branchId: item.branchId,
          state: item.state,
          preparationZone: item.preparationZone.trim() || null,
        })),
        comboComponents: productForm.comboComponents.map((component) => ({
          id: component.id,
          name: component.name,
          selectionType: component.selectionType,
          quantity: Number(component.quantity),
          active: component.active,
          order: Number(component.order),
          products: component.products.map((choice) => ({
            id: choice.id,
            productId: choice.productId,
            extraPriceUsdCents: Math.round(Number(choice.extraPriceUsd || 0) * 100),
            default: choice.default,
            active: choice.active,
            order: Number(choice.order),
          })),
        })),
      };
      await apiRequest(editingProductId ? `/api/business/products/${editingProductId}` : '/api/business/products', {
        method: editingProductId ? 'PATCH' : 'POST', body: jsonBody(payload),
      });
      setProductOpen(false);
      show(editingProductId ? 'Producto actualizado.' : 'Producto creado.', 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.products }),
      ]);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo guardar el producto.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    const available = 2 - productForm.imageKeys.length;
    if (available <= 0) { show('Cada producto admite un máximo de 2 imágenes.', 'error'); return; }
    setUploading(true);
    try {
      const uploaded: Array<{ key: string; publicUrl: string }> = [];
      for (const file of Array.from(files).slice(0, available)) uploaded.push(await uploadImage(file, 'product'));
      setProductForm((current) => ({
        ...current,
        imageKeys: [...current.imageKeys, ...uploaded.map((item) => item.key)],
        imageUrls: [...current.imageUrls, ...uploaded.map((item) => item.publicUrl)],
      }));
      show('Imagen subida.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo subir la imagen.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const uploadCategoryImage = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadImage(file, 'product');
      setCategoryForm((current) => ({ ...current, imageKey: uploaded.key, imageUrl: uploaded.publicUrl }));
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo subir la imagen.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const addIngredient = (initialName = '') => setProductForm((current) => ({
    ...current,
    ingredients: [...current.ingredients, {
      name: initialName,
      includedByDefault: true,
      selectable: true,
      extraPriceUsd: '0',
      active: true,
      order: String(current.ingredients.length),
    }],
  }));
  const addIngredientFromCatalog = (name: string) => {
    const normalized = name.trim();
    if (!normalized) return;
    if (productForm.ingredients.some((ingredient) => ingredient.name.trim().toLowerCase() === normalized.toLowerCase())) {
      show('Ese ingrediente ya está asignado a este producto.', 'error');
      return;
    }
    addIngredient(normalized);
  };
  const updateIngredient = (index: number, patch: Partial<IngredientForm>) => setProductForm((current) => ({
    ...current, ingredients: current.ingredients.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));
  const addVariant = () => setProductForm((current) => ({
    ...current,
    variants: [...current.variants, { id: crypto.randomUUID(), name: '', sku: '', priceUsd: current.priceUsd, active: true, order: String(current.variants.length) }],
  }));
  const updateVariant = (index: number, patch: Partial<VariantForm>) => setProductForm((current) => ({
    ...current, variants: current.variants.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));
  const updateAssignment = (index: number, patch: Partial<AssignmentForm>) => setProductForm((current) => ({
    ...current, optionGroups: current.optionGroups.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));

  const assignGroup = (groupId: string) => {
    if (!groupId) return;
    setProductForm((current) => current.optionGroups.some((item) => item.groupId === groupId) ? current : ({
      ...current,
      optionGroups: [...current.optionGroups, {
        groupId, order: String(current.optionGroups.length), requiredOverride: 'inherit',
        minSelectionsOverride: '', maxSelectionsOverride: '', variantIds: [],
      }],
    }));
  };

  const updateGroupOption = (index: number, patch: Partial<OptionForm>) => setGroupForm((current) => ({
    ...current, options: current.options.map((option, optionIndex) => optionIndex === index ? { ...option, ...patch } : option),
  }));

  const createOptionGroup = async () => {
    if (!groupForm.name.trim() || groupForm.options.some((option) => !option.name.trim())) {
      show('Completa el nombre del grupo y de todas sus opciones.', 'error');
      return;
    }
    setGroupBusy(true);
    try {
      const result = await apiRequest<{ groupId: string }>('/api/business/option-groups', {
        method: 'POST',
        body: jsonBody({
          name: groupForm.name,
          description: groupForm.description,
          selectionType: groupForm.selectionType,
          minSelections: Number(groupForm.minSelections),
          maxSelections: Number(groupForm.maxSelections),
          required: groupForm.required,
          reusable: true,
          active: true,
          order: optionGroups.length,
          options: groupForm.options.map((option) => ({
            name: option.name,
            extraPriceUsdCents: Math.round(Number(option.extraPriceUsd || 0) * 100),
            active: option.active,
            order: Number(option.order),
          })),
        }),
      });
      assignGroup(result.groupId);
      setGroupForm(emptyGroupForm());
      setNewGroupOpen(false);
      show('Grupo creado y asignado.', 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.optionGroups }),
      ]);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo crear el grupo.', 'error');
    } finally {
      setGroupBusy(false);
    }
  };

  const updateAvailability = (branchId: string, patch: Partial<AvailabilityForm>) => setProductForm((current) => ({
    ...current,
    availability: current.availability.map((item) => item.branchId === branchId ? { ...item, ...patch } : item),
  }));

  const addComboComponent = () => setProductForm((current) => ({
    ...current,
    comboComponents: [...current.comboComponents, {
      name: '', selectionType: 'fijo', quantity: '1', active: true,
      order: String(current.comboComponents.length), products: [],
    }],
  }));
  const updateComboComponent = (index: number, patch: Partial<ComboComponentForm>) => setProductForm((current) => ({
    ...current,
    comboComponents: current.comboComponents.map((component, componentIndex) => componentIndex === index ? { ...component, ...patch } : component),
  }));
  const selectComboProduct = (componentIndex: number, productId: string, selected: boolean) => setProductForm((current) => ({
    ...current,
    comboComponents: current.comboComponents.map((component, index) => {
      if (index !== componentIndex) return component;
      const existing = component.products.find((choice) => choice.productId === productId);
      if (!selected) return { ...component, products: component.products.filter((choice) => choice.productId !== productId) };
      const choice = existing ?? { productId, extraPriceUsd: '0', default: component.products.length === 0, active: true, order: String(component.products.length) };
      return { ...component, products: component.selectionType === 'fijo' ? [{ ...choice, default: true, active: true, order: '0' }] : [...component.products, choice] };
    }),
  }));
  const updateComboChoice = (componentIndex: number, productId: string, patch: Partial<ComboChoiceForm>) => setProductForm((current) => ({
    ...current,
    comboComponents: current.comboComponents.map((component, index) => index === componentIndex ? {
      ...component,
      products: component.products.map((choice) => choice.productId === productId ? { ...choice, ...patch } : choice),
    } : component),
  }));

  const deactivate = async (product: Product) => {
    if (!window.confirm(`¿Desactivar ${product.name}? Los pedidos históricos no se alterarán.`)) return;
    try {
      await apiRequest(`/api/business/products/${product.id}`, { method: 'DELETE' });
      show('Producto desactivado.', 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.catalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.menu.products }),
      ]);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo desactivar.', 'error');
    }
  };

  const productsUsingGroup = (groupId: string) => products.filter((p) => p.optionGroups.some((a) => a.groupId === groupId));

  const openCreateGroup = () => {
    setEditingGroupId(null);
    setGroupLibraryForm(emptyGroupForm());
    setGroupModalOpen(true);
  };

  const openEditGroup = (group: OptionGroup) => {
    setEditingGroupId(group.id);
    setGroupLibraryForm({
      name: group.name,
      description: group.description,
      selectionType: group.selectionType,
      minSelections: String(group.minSelections),
      maxSelections: String(group.maxSelections),
      required: group.required,
      options: group.options.map((opt) => ({
        id: opt.id, name: opt.name,
        extraPriceUsd: (opt.extraPriceUsdCents / 100).toFixed(2),
        active: opt.active, order: String(opt.order),
      })),
    });
    setGroupModalOpen(true);
  };

  const updateGroupLibraryOption = (index: number, patch: Partial<OptionForm>) => setGroupLibraryForm((c) => ({
    ...c, options: c.options.map((opt, i) => i === index ? { ...opt, ...patch } : opt),
  }));

  const saveGroupLibrary = async () => {
    if (!groupLibraryForm.name.trim() || groupLibraryForm.options.some((opt) => !opt.name.trim())) {
      show('Completa el nombre del grupo y de todas sus opciones.', 'error');
      return;
    }
    if (!groupLibraryForm.options.length) {
      show('Agrega al menos una opción al grupo.', 'error');
      return;
    }
    const payload = {
      name: groupLibraryForm.name,
      description: groupLibraryForm.description,
      selectionType: groupLibraryForm.selectionType,
      minSelections: Number(groupLibraryForm.minSelections),
      maxSelections: Number(groupLibraryForm.maxSelections),
      required: groupLibraryForm.required,
      reusable: true,
      active: true,
      order: editingGroupId ? 0 : optionGroups.length,
      options: groupLibraryForm.options.map((opt, i) => ({
        id: opt.id, name: opt.name,
        extraPriceUsdCents: Math.round(Number(opt.extraPriceUsd || 0) * 100),
        active: opt.active, order: Number(opt.order || i),
      })),
    };
    try {
      if (editingGroupId) {
        await updateOptionGroupMutation.mutateAsync({ groupId: editingGroupId, payload });
        show('Grupo actualizado.', 'success');
      } else {
        await createOptionGroupMutation.mutateAsync(payload);
        show('Grupo creado.', 'success');
      }
      setGroupModalOpen(false);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo guardar el grupo.', 'error');
    }
  };

  const deactivateGroup = async (group: OptionGroup) => {
    const usedBy = productsUsingGroup(group.id);
    if (usedBy.length > 0) {
      show(`No se puede desactivar: "${group.name}" está asignado a ${usedBy.length} producto(s): ${usedBy.map((p) => p.name).join(', ')}.`, 'error');
      return;
    }
    if (!window.confirm(`¿Desactivar el grupo "${group.name}"?`)) return;
    try {
      await deactivateOptionGroupMutation.mutateAsync(group.id);
      show('Grupo desactivado.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo desactivar el grupo.', 'error');
    }
  };

  const productPrice = (product: Product) => {
    const activePrices = product.variants.filter((variant) => variant.active).map((variant) => variant.priceUsdCents);
    return product.priceMode === 'variante' && activePrices.length ? `Desde ${formatUsd(Math.min(...activePrices))}` : formatUsd(product.priceUsdCents);
  };

  if (loading) return <LoadingScreen />;
  return (
    <div>
      <PageHeader title="Menú" description="Productos, configuraciones, disponibilidad y zonas de preparación." action={<div className="flex gap-2"><Button variant="secondary" onClick={newCategory}>+ Categoría</Button><Button onClick={newProduct}>+ Producto</Button></div>} />

      <div className="mb-5 flex flex-wrap gap-2">
        {categories.map((category, index) => <div key={category.id} className="flex items-center gap-1 rounded-full border bg-white px-2 py-1 text-sm font-semibold">
          <button type="button" onClick={() => editCategory(category)} className="px-2 py-1 hover:text-blue-700">{category.name} · {category.active ? 'activa' : 'inactiva'}</button>
          <button type="button" aria-label={`Subir categoría ${category.name}`} disabled={index === 0 || reorderCategories.isPending} onClick={() => void reorderCategory(category.id, -1)} className="rounded-full px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↑</button>
          <button type="button" aria-label={`Bajar categoría ${category.name}`} disabled={index === categories.length - 1 || reorderCategories.isPending} onClick={() => void reorderCategory(category.id, 1)} className="rounded-full px-2 py-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↓</button>
        </div>)}
      </div>

      <details className="mb-5" open={groupLibraryOpen} onToggle={(e) => setGroupLibraryOpen(e.currentTarget.open)}>
        <summary className="cursor-pointer select-none rounded-2xl border border-slate-200 bg-white px-5 py-4 font-black text-slate-950 hover:bg-slate-50">
          Grupos de opciones · {optionGroups.filter((g) => g.active).length} activos
        </summary>
        <div className="mt-3 grid gap-3">
          <div className="flex justify-end">
            <Button variant="secondary" onClick={openCreateGroup}>+ Nuevo grupo</Button>
          </div>
          {optionGroups.filter((g) => g.active).map((group) => {
            const usedBy = productsUsingGroup(group.id);
            return (
              <Card key={group.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-black">{group.name}</h4>
                    <Badge tone={group.required ? 'amber' : 'slate'}>{group.selectionType === 'single' ? 'una opción' : group.selectionType === 'multiple' ? 'múltiple' : 'cantidad'}</Badge>
                  </div>
                  {group.description && <p className="mt-1 text-xs text-slate-500 line-clamp-1">{group.description}</p>}
                  <p className="mt-1 text-xs text-slate-400">{group.options.filter((o) => o.active).length} opciones{usedBy.length > 0 ? ` · ${usedBy.length} producto(s)` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => openEditGroup(group)}>Editar</Button>
                  <Button variant="ghost" onClick={() => void deactivateGroup(group)}>Desactivar</Button>
                </div>
              </Card>
            );
          })}
          {!optionGroups.filter((g) => g.active).length && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">No hay grupos de opciones creados.</p>}
        </div>
      </details>

      <Card className="mb-5">
        <div className="grid gap-3 lg:grid-cols-[1fr_240px_auto] lg:items-center">
          <Input placeholder="Buscar producto" value={search} onChange={(event) => setSearch(event.target.value)} />
          <HeroSelectField value={categoryFilter || null} onChange={(value) => setCategoryFilter(String(value ?? ''))} options={[{ id: '', label: 'Todas las categorías' }, ...categories.map((category) => ({ id: category.id, label: category.name }))]} />
          <Switch label="Solo problemas" description={`Problemas detectados: ${productsWithIssuesCount} productos`} isSelected={onlyMenuProblems} onChange={setOnlyMenuProblems} />
        </div>
      </Card>

      {selectedCount > 0 && <Card className="mb-5 border-blue-200 bg-blue-50/40">
        <div className="grid gap-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div><h3 className="font-black text-slate-950">Edición masiva</h3><p className="text-sm text-slate-600">{selectedCount} seleccionados</p></div>
            <Button variant="ghost" onClick={clearBulkSelection}>Limpiar selección</Button>
          </div>
          <div className="grid gap-3 lg:grid-cols-4 lg:items-end">
            <Field label="Estado"><HeroSelectField value={bulkActiveAction || null} onChange={(value) => setBulkActiveAction((value ?? 'keep') as BulkActiveAction)} options={[{ id: 'keep', label: 'Mantener estado' }, { id: 'activate', label: 'Activar' }, { id: 'deactivate', label: 'Desactivar' }]} /></Field>
            <Field label="Categoría"><HeroSelectField value={bulkCategoryId || null} onChange={(value) => setBulkCategoryId(String(value ?? '__keep__'))} options={[{ id: '__keep__', label: 'Mantener categoría' }, { id: '__none__', label: 'Sin categoría' }, ...categories.map((category) => ({ id: category.id, label: category.name }))]} /></Field>
            <Field label="Zona de preparación" hint="Dejá vacío para no cambiarla."><Input maxLength={80} placeholder="Mantener zona" value={bulkPreparationZone} onChange={(event) => setBulkPreparationZone(event.target.value)} /></Field>
            <Button onClick={() => void applyBulkChanges()} busy={bulkUpdateProducts.isPending}>Aplicar cambios</Button>
          </div>
          <div className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="text-sm font-black text-amber-950">Sugerencias IA determinísticas</p><p className="text-xs text-amber-800">Mejora alias, etiquetas, rol y descripción usando datos del menú. No usa un LLM externo.</p></div>
            <Button variant="secondary" onClick={() => void generateSelectedAiMetadata()} busy={generateProductAiMetadata.isPending}>Generar sugerencias</Button>
          </div>
        </div>
      </Card>}

      {filtered.length ? <><div className="mb-3 flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <Checkbox label="Seleccionar productos visibles" isSelected={allVisibleSelected} onChange={toggleVisibleSelection} />
        <span className="text-xs font-bold text-slate-500">{filtered.length} visibles · ordená con ↑ ↓</span>
      </div><div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{filtered.map((product, index) => {
        const productIssues = productIssuesById.get(product.id) ?? [];
        return (
          <Card key={product.id} className={!product.active ? 'opacity-65' : ''}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex gap-1">
                <button type="button" aria-label={`Subir producto ${product.name}`} disabled={index === 0 || reorderProducts.isPending} onClick={() => void reorderVisibleProduct(product.id, -1)} className="rounded-full border px-2 py-1 text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-30">↑</button>
                <button type="button" aria-label={`Bajar producto ${product.name}`} disabled={index === filtered.length - 1 || reorderProducts.isPending} onClick={() => void reorderVisibleProduct(product.id, 1)} className="rounded-full border px-2 py-1 text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-30">↓</button>
              </div>
              <Checkbox label="Seleccionar producto" isSelected={selectedProductIds.has(product.id)} onChange={(selected) => toggleProductSelection(product.id, selected)} />
            </div>
            <div className="flex gap-4">
              {product.imageUrls[0] ? <img src={product.imageUrls[0]} alt="" className="h-24 w-24 rounded-2xl object-cover" /> : <div className="grid h-24 w-24 shrink-0 place-items-center rounded-2xl bg-slate-100 text-3xl">🍽️</div>}
              <div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-2"><h2 className="truncate font-black">{product.name}</h2><Badge tone={product.active ? 'green' : 'slate'}>{product.active ? 'activo' : 'inactivo'}</Badge></div><p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{product.description || 'Sin descripción'}</p><div className="mt-2 font-black">{productPrice(product)} {product.promoType !== 'none' && <span className="text-xs text-rose-600">· promoción</span>}</div></div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500"><Badge tone={product.type === 'combo' ? 'purple' : product.priceMode === 'variante' ? 'blue' : 'slate'}>{product.type === 'combo' ? 'combo' : product.priceMode === 'variante' ? 'variantes' : 'simple'}</Badge><Badge>{product.preparationZone || 'Sin zona'}</Badge><span>{product.ingredients.length} ingredientes</span><span>{product.optionGroups.length} grupos</span><span>{product.imageKeys.length}/2 imágenes</span></div>
            {productIssues.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{productIssues.map((issue) => <Badge key={issue.label} tone={issue.tone}>{issue.label}</Badge>)}</div>}
            <div className="mt-4 flex gap-2"><Button variant="secondary" onClick={() => editProduct(product)}>Editar</Button><Button variant="secondary" onClick={() => duplicateProduct(product)}>Duplicar</Button>{product.active && <Button variant="ghost" onClick={() => void deactivate(product)}>Desactivar</Button>}</div>
          </Card>
        );
      })}</div></> : <EmptyState title={onlyMenuProblems ? 'Sin problemas' : 'Sin productos'} description={onlyMenuProblems ? 'Sin problemas del menú con los filtros actuales.' : 'Crea productos para publicar el menú.'} action={onlyMenuProblems ? undefined : <Button onClick={newProduct}>Crear producto</Button>} />}

      <Modal open={categoryOpen} onClose={() => setCategoryOpen(false)} title={editingCategoryId ? 'Editar categoría' : 'Nueva categoría'}>
        <form onSubmit={saveCategory} className="grid gap-4">
          <Field label="Nombre"><Input required value={categoryForm.name} onChange={(event) => setCategoryForm({ ...categoryForm, name: event.target.value })} /></Field>
          <Field label="Descripción"><Textarea value={categoryForm.description} onChange={(event) => setCategoryForm({ ...categoryForm, description: event.target.value })} /></Field>
          <EditorSection title="Asistente IA" description="Ayuda al chat a entender nombres comerciales o ambiguos.">
            <div className="grid gap-4">
              <Field label="Alias semánticos" hint="Separados por coma. Ejemplo: pollo, chicken wings, alitas"><Input maxLength={500} placeholder="pollo, chicken wings, alitas" value={categoryForm.aiMetadata.aiAliases} onChange={(event) => setCategoryForm({ ...categoryForm, aiMetadata: { ...categoryForm.aiMetadata, aiAliases: event.target.value } })} /></Field>
              <div className="grid gap-4 sm:grid-cols-3"><Field label="Rol"><HeroSelectField value={categoryForm.aiMetadata.aiRole || null} onChange={(value) => setCategoryForm({ ...categoryForm, aiMetadata: { ...categoryForm.aiMetadata, aiRole: (value ?? 'unknown') as MenuAiRole } })} options={aiRoleOptions} /></Field><Field label="Prioridad IA" hint="0 baja, 5 alta"><Input type="number" min="0" max="5" step="1" value={categoryForm.aiMetadata.aiPriority} onChange={(event) => setCategoryForm({ ...categoryForm, aiMetadata: { ...categoryForm.aiMetadata, aiPriority: event.target.value } })} /></Field><div className="flex items-end pb-2"><Checkbox label="Mostrar en resumen IA" isSelected={categoryForm.aiMetadata.showInAiOverview} onChange={(showInAiOverview) => setCategoryForm({ ...categoryForm, aiMetadata: { ...categoryForm.aiMetadata, showInAiOverview } })} /></div></div>
              <Field label="Nota para el asistente"><Textarea maxLength={300} placeholder="Ej. Esta categoría agrupa platos principales de pollo." value={categoryForm.aiMetadata.aiDescription} onChange={(event) => setCategoryForm({ ...categoryForm, aiMetadata: { ...categoryForm.aiMetadata, aiDescription: event.target.value } })} /></Field>
            </div>
          </EditorSection>
          <div className="flex items-center gap-3">
            {categoryForm.imageUrl && <img src={categoryForm.imageUrl} alt="" className="h-20 w-28 rounded-xl border object-cover" />}
            <label className="cursor-pointer rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50">{uploading ? 'Subiendo...' : categoryForm.imageUrl ? 'Cambiar imagen' : 'Agregar imagen'}<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={uploading} className="hidden" onChange={(event) => void uploadCategoryImage(event.target.files?.[0])} /></label>
            {categoryForm.imageKey && <button type="button" className="text-sm font-bold text-rose-600" onClick={() => setCategoryForm({ ...categoryForm, imageKey: null, imageUrl: null })}>Quitar</button>}
          </div>
          <Field label="Orden"><Input type="number" min="0" value={categoryForm.order} onChange={(event) => setCategoryForm({ ...categoryForm, order: event.target.value })} /></Field>
          <Checkbox label="Categoría activa" isSelected={categoryForm.active} onChange={(active) => setCategoryForm({ ...categoryForm, active })} />
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setCategoryOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Guardar</Button></div>
        </form>
      </Modal>

      <Modal open={groupModalOpen} onClose={() => setGroupModalOpen(false)} title={editingGroupId ? 'Editar grupo de opciones' : 'Nuevo grupo de opciones'} width="max-w-3xl">
        <form onSubmit={(e) => { e.preventDefault(); void saveGroupLibrary(); }} className="grid gap-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nombre"><Input required maxLength={100} value={groupLibraryForm.name} onChange={(e) => setGroupLibraryForm({ ...groupLibraryForm, name: e.target.value })} /></Field>
            <Field label="Tipo de selección"><HeroSelectField value={groupLibraryForm.selectionType || null} onChange={(value) => { const st = (value ?? '') as SelectionType; setGroupLibraryForm({ ...groupLibraryForm, selectionType: st, maxSelections: st === 'single' ? '1' : groupLibraryForm.maxSelections }); }} options={[{ id: 'single', label: 'Una opción' }, { id: 'multiple', label: 'Varias opciones' }, { id: 'quantity', label: 'Opciones con cantidad' }]} /></Field>
          </div>
          <Field label="Descripción"><Input maxLength={300} value={groupLibraryForm.description} onChange={(e) => setGroupLibraryForm({ ...groupLibraryForm, description: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Mínimo"><Input type="number" min="0" max="99" value={groupLibraryForm.minSelections} onChange={(e) => setGroupLibraryForm({ ...groupLibraryForm, minSelections: e.target.value })} /></Field>
            <Field label="Máximo"><Input type="number" min="1" max="99" disabled={groupLibraryForm.selectionType === 'single'} value={groupLibraryForm.maxSelections} onChange={(e) => setGroupLibraryForm({ ...groupLibraryForm, maxSelections: e.target.value })} /></Field>
            <div className="flex items-end pb-2"><Checkbox label="Requerido" isSelected={groupLibraryForm.required} onChange={(required) => setGroupLibraryForm({ ...groupLibraryForm, required })} /></div>
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-black">Opciones</h5>
              <button type="button" className="text-sm font-bold text-blue-700" onClick={() => setGroupLibraryForm((c) => ({ ...c, options: [...c.options, { name: '', extraPriceUsd: '0', active: true, order: String(c.options.length) }] }))}>+ Opción</button>
            </div>
            {groupLibraryForm.options.map((option, index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-[1fr_140px_auto]">
                <Input placeholder="Nombre" required value={option.name} onChange={(e) => updateGroupLibraryOption(index, { name: e.target.value })} />
                <Input aria-label="Extra USD" type="number" min="0" step="0.01" value={option.extraPriceUsd} onChange={(e) => updateGroupLibraryOption(index, { extraPriceUsd: e.target.value })} />
                <button type="button" className="px-2 text-sm font-bold text-rose-600" onClick={() => setGroupLibraryForm((c) => ({ ...c, options: c.options.filter((_, i) => i !== index) }))}>Eliminar</button>
              </div>
            ))}
            {!groupLibraryForm.options.length && <p className="text-sm text-slate-500">Agrega al menos una opción.</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setGroupModalOpen(false)}>Cancelar</Button>
            <Button type="submit" busy={createOptionGroupMutation.isPending || updateOptionGroupMutation.isPending}>{editingGroupId ? 'Guardar cambios' : 'Crear grupo'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={productOpen} onClose={() => setProductOpen(false)} title={editingProductId ? 'Editar producto' : 'Nuevo producto'} width="max-w-6xl">
        {!saleStyle ? (
          <div>
            <div className="mb-6"><p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Paso 1 de 2</p><h2 className="mt-2 text-2xl font-black text-slate-950">¿Cómo vende este producto?</h2><p className="mt-1 text-sm text-slate-500">Elige el modelo que mejor describe cómo lo verá el cliente.</p></div>
            <div className="grid gap-3 sm:grid-cols-2">{saleStyles.map((style) => <button key={style.id} type="button" onClick={() => applySaleStyle(style.id)} className="group rounded-2xl border-2 border-slate-200 p-5 text-left transition hover:border-blue-500 hover:bg-blue-50/50"><span className="font-black text-slate-950 group-hover:text-blue-700">{style.title}</span><span className="mt-2 block text-sm text-slate-600">{style.description}</span><span className="mt-3 block text-xs font-semibold text-slate-400">Ejemplo: {style.example}</span></button>)}</div>
            <div className="mt-6 flex justify-end"><Button variant="secondary" onClick={() => setProductOpen(false)}>Cancelar</Button></div>
          </div>
        ) : (
          <form onSubmit={saveProduct} className="grid gap-4">
            <div className="flex flex-col gap-3 rounded-2xl bg-slate-950 p-4 text-white sm:flex-row sm:items-center sm:justify-between">
              <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">Modelo de venta</p><p className="mt-1 font-black">{saleStyles.find((style) => style.id === saleStyle)?.title}</p></div>
              <Button type="button" variant="secondary" onClick={() => setSaleStyle(null)}>Cambiar modelo</Button>
            </div>

            <EditorSection title="1. Información general" description="Identidad, imágenes, precio, unidad y publicación." open>
              <div className="grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2"><Field label="Nombre"><Input required maxLength={120} value={productForm.name} onChange={(event) => setProductForm({ ...productForm, name: event.target.value })} /></Field><Field label="Categoría"><HeroSelectField value={productForm.categoryId || null} onChange={(value) => setProductForm({ ...productForm, categoryId: String(value ?? '') })} options={[{ id: '', label: 'Sin categoría' }, ...categories.map((category) => ({ id: category.id, label: category.name }))]} /></Field></div>
                <Field label="Descripción"><Textarea maxLength={1000} value={productForm.description} onChange={(event) => setProductForm({ ...productForm, description: event.target.value })} /></Field>
                <div><div className="mb-2 flex items-center justify-between"><div><h4 className="font-bold">Imágenes ({productForm.imageKeys.length}/2)</h4><p className="text-xs text-slate-500">La primera será la portada.</p></div><label className="cursor-pointer rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50">{uploading ? 'Subiendo...' : '+ Subir'}<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/avif" disabled={uploading || productForm.imageKeys.length >= 2} className="hidden" onChange={(event) => void upload(event.target.files)} /></label></div><div className="flex gap-3">{productForm.imageUrls.map((url, index) => <div key={`${url}-${index}`} className="relative"><img src={url} alt="" className="h-28 w-28 rounded-2xl border object-cover" /><button type="button" aria-label="Eliminar imagen" className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-rose-600 text-white" onClick={() => setProductForm((current) => ({ ...current, imageKeys: current.imageKeys.filter((_, itemIndex) => itemIndex !== index), imageUrls: current.imageUrls.filter((_, itemIndex) => itemIndex !== index) }))}>×</button></div>)}</div></div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"><Field label="Tipo"><HeroSelectField value={productForm.type || null} onChange={() => {}} isDisabled options={[{ id: 'simple', label: 'Producto simple' }, { id: 'combo', label: 'Combo' }]} /></Field><Field label="Modo de precio"><HeroSelectField value={productForm.priceMode || null} onChange={() => {}} isDisabled options={[{ id: 'fijo', label: 'Precio fijo' }, { id: 'variante', label: 'Por variante' }]} /></Field><Field label={productForm.priceMode === 'variante' ? 'Precio base USD' : 'Precio USD'} hint={productForm.priceMode === 'variante' ? 'Referencia y límite de promoción; cada variante define su precio.' : undefined}><Input type="number" min="0" step="0.01" required value={productForm.priceUsd} onChange={(event) => setProductForm({ ...productForm, priceUsd: event.target.value })} /></Field><Field label="Zona de preparación"><Input required maxLength={80} value={productForm.preparationZone} onChange={(event) => setProductForm({ ...productForm, preparationZone: event.target.value })} /></Field></div>
                <div className="grid gap-4 sm:grid-cols-2"><Field label="Tiempo de preparación esperado (minutos)" hint="Opcional. Solo los productos con tiempo configurado participan en las métricas de preparación."><Input type="number" min="1" max="1440" step="1" value={productForm.preparationTimeMinutes} onChange={(event) => setProductForm({ ...productForm, preparationTimeMinutes: event.target.value })} /></Field></div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"><Field label="Promoción"><HeroSelectField value={productForm.promoType || null} onChange={(value) => setProductForm({ ...productForm, promoType: (value ?? '') as PromoType, promoValue: '0' })} options={[{ id: 'none', label: 'Sin promoción' }, { id: 'percentage', label: 'Descuento porcentual' }, { id: 'fixed', label: 'Precio promocional fijo' }]} /></Field><Field label={productForm.promoType === 'percentage' ? 'Porcentaje' : 'Precio promo USD'}><Input type="number" min="0" max={productForm.promoType === 'percentage' ? '100' : undefined} step={productForm.promoType === 'percentage' ? '0.1' : '0.01'} disabled={productForm.promoType === 'none'} value={productForm.promoValue} onChange={(event) => setProductForm({ ...productForm, promoValue: event.target.value })} /></Field><Field label="Unidad de venta"><HeroSelectField value={productForm.saleUnit || null} onChange={(value) => setProductForm({ ...productForm, saleUnit: (value ?? '') as SaleUnit })} options={[{ id: 'unidad', label: 'Unidad' }, { id: 'porcion', label: 'Porción' }, { id: 'otra', label: 'Otra' }]} /></Field><Field label="Nombre de unidad" hint="Ejemplo: bandeja, kilo, botella"><Input required={productForm.saleUnit === 'otra'} disabled={productForm.saleUnit !== 'otra'} maxLength={40} value={productForm.saleUnitName} onChange={(event) => setProductForm({ ...productForm, saleUnitName: event.target.value })} /></Field></div>
                <div className="grid gap-4 sm:grid-cols-2"><Field label="Orden"><Input type="number" min="0" value={productForm.order} onChange={(event) => setProductForm({ ...productForm, order: event.target.value })} /></Field><div className="flex items-end pb-2"><Checkbox label="Producto activo y visible" isSelected={productForm.active} onChange={(active) => setProductForm({ ...productForm, active })} /></div></div>
              </div>
            </EditorSection>

            <EditorSection title="Asistente IA" description="Recomendaciones, búsqueda semántica y resumen del menú.">
              <div className="grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2"><Field label="Alias semánticos" hint="Separados por coma. Ejemplo: pollo, chicken wings, alitas"><Input maxLength={500} placeholder="pollo, chicken wings, alitas" value={productForm.aiMetadata.aiAliases} onChange={(event) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, aiAliases: event.target.value } })} /></Field><Field label="Etiquetas comerciales" hint="Separadas por coma. Ejemplo: principal, para dos, combo"><Input maxLength={500} placeholder="principal, para dos, combo" value={productForm.aiMetadata.aiTags} onChange={(event) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, aiTags: event.target.value } })} /></Field></div>
                <div className="grid gap-4 sm:grid-cols-3"><Field label="Rol"><HeroSelectField value={productForm.aiMetadata.aiRole || null} onChange={(value) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, aiRole: (value ?? 'unknown') as MenuAiRole } })} options={aiRoleOptions} /></Field><Field label="Prioridad IA" hint="0 baja, 5 alta"><Input type="number" min="0" max="5" step="1" value={productForm.aiMetadata.aiPriority} onChange={(event) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, aiPriority: event.target.value } })} /></Field><div className="flex items-end pb-2"><Checkbox label="Mostrar en resumen IA" isSelected={productForm.aiMetadata.showInAiOverview} onChange={(showInAiOverview) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, showInAiOverview } })} /></div></div>
                <Field label="Nota para el asistente"><Textarea maxLength={300} placeholder="Ej. Producto principal recomendado para almuerzo o cena." value={productForm.aiMetadata.aiDescription} onChange={(event) => setProductForm({ ...productForm, aiMetadata: { ...productForm.aiMetadata, aiDescription: event.target.value } })} /></Field>
              </div>
            </EditorSection>

            {productForm.priceMode === 'variante' && <EditorSection title="2. Tamaños o presentaciones" description="Define el precio, SKU y estado de cada variante." open>
              <div className="grid gap-3">
                {productForm.variants.map((variant, index) => <div key={variant.id ?? index} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 lg:grid-cols-[1.2fr_1fr_.7fr_auto]"><Field label="Nombre"><Input required maxLength={80} value={variant.name} onChange={(event) => updateVariant(index, { name: event.target.value })} /></Field><Field label="SKU (opcional)"><Input maxLength={80} value={variant.sku ?? ''} onChange={(event) => updateVariant(index, { sku: event.target.value })} /></Field><Field label="Precio USD"><Input required type="number" min="0" step="0.01" value={variant.priceUsd} onChange={(event) => updateVariant(index, { priceUsd: event.target.value })} /></Field><div className="flex items-end gap-2 pb-1"><Checkbox label="Activa" isSelected={variant.active} onChange={(active) => updateVariant(index, { active })} /><button type="button" className="text-sm font-bold text-rose-600" onClick={() => setProductForm((current) => ({ ...current, variants: current.variants.filter((_, itemIndex) => itemIndex !== index), optionGroups: current.optionGroups.map((assignment) => ({ ...assignment, variantIds: assignment.variantIds.filter((id) => id !== variant.id) })) }))}>Eliminar</button></div></div>)}
                {!productForm.variants.length && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">Agrega al menos una variante activa.</p>}
                <div><Button type="button" variant="secondary" onClick={addVariant}>+ Agregar variante</Button></div>
              </div>
            </EditorSection>}

            <EditorSection title={`${productForm.priceMode === 'variante' ? '3' : '2'}. Ingredientes incluidos`} description="Indica qué incluye el producto y qué puede excluir el cliente.">
              <div className="grid gap-3">
                {ingredientCatalogNames.length > 0 && (
                  <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Ingrediente reutilizable</label>
                      <HeroSelectField value={ingredientCatalogKey || null} onChange={(value) => { const selected = String(value ?? ''); if (selected) { addIngredientFromCatalog(selected); setIngredientCatalogKey(''); } }} options={[{ id: '_placeholder', label: 'Selecciona un ingrediente existente...', isDisabled: true }, ...ingredientCatalogNames.map((name) => ({ id: name, label: name }))]} />
                    </div>
                    <Button type="button" variant="secondary" onClick={() => addIngredient()}>+ Nuevo ingrediente</Button>
                  </div>
                )}
                {productForm.ingredients.map((ingredient, index) => {
                  const ingredientListId = `ingredient-options-${index}`;
                  return <div key={ingredient.id ?? index} className="grid gap-3 rounded-2xl border border-slate-200 p-4 lg:grid-cols-[1.4fr_.6fr_auto]"><Field label="Ingrediente"><Input required maxLength={80} list={ingredientListId} value={ingredient.name} onChange={(event) => updateIngredient(index, { name: event.target.value })} /></Field><datalist id={ingredientListId}>{ingredientCatalogNames.map((name) => <option key={name} value={name} />)}</datalist><Field label="Extra USD"><Input type="number" min="0" step="0.01" value={ingredient.extraPriceUsd} onChange={(event) => updateIngredient(index, { extraPriceUsd: event.target.value })} /></Field><button type="button" className="self-end rounded-xl px-3 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50" onClick={() => setProductForm((current) => ({ ...current, ingredients: current.ingredients.filter((_, itemIndex) => itemIndex !== index) }))}>Eliminar</button><Checkbox label="Incluido por defecto" isSelected={ingredient.includedByDefault} onChange={(includedByDefault) => updateIngredient(index, { includedByDefault })} /><Checkbox label="Cliente puede excluirlo" isSelected={ingredient.selectable} onChange={(selectable) => updateIngredient(index, { selectable })} /><Checkbox label="Activo" isSelected={ingredient.active} onChange={(active) => updateIngredient(index, { active })} /></div>;
                })}
                {ingredientCatalogNames.length === 0 && <div><Button type="button" variant="secondary" onClick={() => addIngredient()}>+ Agregar ingrediente</Button></div>}
              </div>
            </EditorSection>

            {(saleStyle === 'configurable' || saleStyle === 'variants') && <EditorSection title={`${productForm.priceMode === 'variante' ? '4' : '3'}. Grupos de opciones`} description="Reutiliza grupos, ajusta sus reglas y limita por variante." open={saleStyle === 'configurable'}>
              <div className="grid gap-4">
                <div className="flex flex-col gap-2 sm:flex-row"><HeroSelectField value={groupAssignKey || null} onChange={(value) => { const selected = String(value ?? ''); if (selected) { assignGroup(selected); setGroupAssignKey(''); } }} options={[{ id: '_placeholder', label: 'Seleccionar grupo existente...', isDisabled: true }, ...optionGroups.filter((group) => group.active && !productForm.optionGroups.some((assignment) => assignment.groupId === group.id)).map((group) => ({ id: group.id, label: `${group.name} · ${group.options.length} opciones` }))]} /><Button type="button" variant="secondary" onClick={() => setNewGroupOpen((open) => !open)}>{newGroupOpen ? 'Cerrar creación' : '+ Crear grupo'}</Button></div>

                {newGroupOpen && <div className="grid gap-4 rounded-2xl border-2 border-blue-200 bg-blue-50/40 p-4 sm:p-5"><div><h4 className="font-black">Nuevo grupo reutilizable</h4><p className="text-xs text-slate-500">Se guarda en la biblioteca y se asigna a este producto.</p></div><div className="grid gap-3 sm:grid-cols-2"><Field label="Nombre"><Input maxLength={100} value={groupForm.name} onChange={(event) => setGroupForm({ ...groupForm, name: event.target.value })} /></Field><Field label="Tipo de selección"><HeroSelectField value={groupForm.selectionType || null} onChange={(value) => { const selectionType = (value ?? '') as SelectionType; setGroupForm({ ...groupForm, selectionType, maxSelections: selectionType === 'single' ? '1' : groupForm.maxSelections }); }} options={[{ id: 'single', label: 'Una opción' }, { id: 'multiple', label: 'Varias opciones' }, { id: 'quantity', label: 'Opciones con cantidad' }]} /></Field></div><Field label="Descripción"><Input maxLength={300} value={groupForm.description} onChange={(event) => setGroupForm({ ...groupForm, description: event.target.value })} /></Field><div className="grid gap-3 sm:grid-cols-3"><Field label="Mínimo"><Input type="number" min="0" max="99" value={groupForm.minSelections} onChange={(event) => setGroupForm({ ...groupForm, minSelections: event.target.value })} /></Field><Field label="Máximo"><Input type="number" min="1" max="99" disabled={groupForm.selectionType === 'single'} value={groupForm.maxSelections} onChange={(event) => setGroupForm({ ...groupForm, maxSelections: event.target.value })} /></Field><div className="flex items-end pb-2"><Checkbox label="Selección requerida" isSelected={groupForm.required} onChange={(required) => setGroupForm({ ...groupForm, required })} /></div></div><div className="grid gap-2"><div className="flex items-center justify-between"><h5 className="text-sm font-black">Opciones y extras</h5><button type="button" className="text-sm font-bold text-blue-700" onClick={() => setGroupForm((current) => ({ ...current, options: [...current.options, { name: '', extraPriceUsd: '0', active: true, order: String(current.options.length) }] }))}>+ Opción</button></div>{groupForm.options.map((option, index) => <div key={index} className="grid gap-2 sm:grid-cols-[1fr_160px_auto]"><Input placeholder="Nombre de opción" value={option.name} onChange={(event) => updateGroupOption(index, { name: event.target.value })} /><Input aria-label="Extra USD" type="number" min="0" step="0.01" value={option.extraPriceUsd} onChange={(event) => updateGroupOption(index, { extraPriceUsd: event.target.value })} /><button type="button" className="px-2 text-sm font-bold text-rose-600" onClick={() => setGroupForm((current) => ({ ...current, options: current.options.filter((_, itemIndex) => itemIndex !== index) }))}>Eliminar</button></div>)}</div><div className="flex justify-end"><Button type="button" busy={groupBusy} onClick={() => void createOptionGroup()}>Crear y asignar</Button></div></div>}

                {productForm.optionGroups.map((assignment, index) => {
                  const group = optionGroups.find((item) => item.id === assignment.groupId);
                  if (!group) return null;
                  return <div key={assignment.id ?? assignment.groupId} className="rounded-2xl border border-slate-200 p-4"><div className="flex items-start justify-between gap-3"><div><h4 className="font-black">{group.name}</h4><p className="mt-1 text-xs text-slate-500">{group.selectionType === 'single' ? 'Una opción' : group.selectionType === 'multiple' ? 'Varias opciones' : 'Opciones con cantidad'} · {group.options.map((option) => option.name).join(', ') || 'sin opciones'}</p></div><button type="button" className="text-sm font-bold text-rose-600" onClick={() => setProductForm((current) => ({ ...current, optionGroups: current.optionGroups.filter((_, itemIndex) => itemIndex !== index) }))}>Quitar</button></div><div className="mt-4 grid gap-3 sm:grid-cols-3"><Field label="Requerido"><HeroSelectField value={assignment.requiredOverride || null} onChange={(value) => updateAssignment(index, { requiredOverride: (value ?? '') as AssignmentForm['requiredOverride'] })} options={[{ id: 'inherit', label: `Heredar (${group.required ? 'sí' : 'no'})` }, { id: 'true', label: 'Sí' }, { id: 'false', label: 'No' }]} /></Field><Field label={`Mínimo (base ${group.minSelections})`}><Input type="number" min="0" max="99" placeholder="Heredar" value={assignment.minSelectionsOverride} onChange={(event) => updateAssignment(index, { minSelectionsOverride: event.target.value })} /></Field><Field label={`Máximo (base ${group.maxSelections})`}><Input type="number" min="1" max="99" placeholder="Heredar" value={assignment.maxSelectionsOverride} onChange={(event) => updateAssignment(index, { maxSelectionsOverride: event.target.value })} /></Field></div>{productForm.priceMode === 'variante' && <div className="mt-4"><p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Disponible en variantes (ninguna = todas)</p><div className="flex flex-wrap gap-3">{productForm.variants.filter((variant) => variant.id).map((variant) => <Checkbox key={variant.id} label={variant.name || 'Sin nombre'} isSelected={variant.id ? assignment.variantIds.includes(variant.id) : false} onChange={(checked) => variant.id && updateAssignment(index, { variantIds: checked ? [...assignment.variantIds, variant.id] : assignment.variantIds.filter((id) => id !== variant.id) })} />)}{productForm.variants.some((variant) => !variant.id) && <span className="text-xs text-slate-500">Guarda primero para condicionar variantes nuevas.</span>}</div></div>}</div>;
                })}
                {!productForm.optionGroups.length && !newGroupOpen && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">Aún no hay grupos asignados.</p>}
              </div>
            </EditorSection>}

            <EditorSection title={`${productForm.priceMode === 'variante' ? (saleStyle === 'variants' ? '5' : '4') : saleStyle === 'configurable' ? '4' : '3'}. Disponibilidad por sucursal`} description="Controla venta y zona de preparación para cada ubicación.">
              <div className="grid gap-3">{branches.map((branch) => {
                const availability = productForm.availability.find((item) => item.branchId === branch.id);
                if (!availability) return null;
                return <div key={branch.id} className="grid gap-3 rounded-2xl border border-slate-200 p-4 sm:grid-cols-[1fr_190px_1fr]"><div><p className="font-black">{branch.name}</p><p className="text-xs text-slate-500">{branch.active ? 'Sucursal activa' : 'Sucursal inactiva'}</p></div><Field label="Estado"><HeroSelectField value={availability.state || null} onChange={(value) => updateAvailability(branch.id, { state: (value ?? '') as BranchState })} options={[{ id: 'disponible', label: 'Disponible' }, { id: 'agotado', label: 'Agotado' }, { id: 'desactivado', label: 'No se vende aquí' }]} /></Field><Field label="Zona override" hint={`Vacío: ${productForm.preparationZone}`}><Input maxLength={80} placeholder={productForm.preparationZone} value={availability.preparationZone} onChange={(event) => updateAvailability(branch.id, { preparationZone: event.target.value })} /></Field></div>;
              })}{!branches.length && <p className="text-sm text-slate-500">No hay sucursales configuradas.</p>}</div>
            </EditorSection>

            {productForm.type === 'combo' && <EditorSection title="4. Componentes del combo" description="Agrega productos fijos o permite que el cliente elija." open>
              <div className="grid gap-4">
                {productForm.comboComponents.map((component, componentIndex) => <div key={component.id ?? componentIndex} className="rounded-2xl border-2 border-slate-200 p-4 sm:p-5"><div className="grid gap-3 lg:grid-cols-[1.2fr_180px_130px_auto]"><Field label="Componente"><Input required maxLength={100} placeholder="Ej. Bebida" value={component.name} onChange={(event) => updateComboComponent(componentIndex, { name: event.target.value })} /></Field><Field label="Modalidad"><HeroSelectField value={component.selectionType || null} onChange={(value) => { const selectionType = (value ?? '') as ComboComponentForm['selectionType']; updateComboComponent(componentIndex, { selectionType, products: selectionType === 'fijo' ? component.products.slice(0, 1).map((choice) => ({ ...choice, default: true, active: true })) : component.products }); }} options={[{ id: 'fijo', label: 'Producto fijo' }, { id: 'eleccion', label: 'A elección' }]} /></Field><Field label="Cantidad"><Input type="number" min="1" max="20" value={component.quantity} onChange={(event) => updateComboComponent(componentIndex, { quantity: event.target.value })} /></Field><button type="button" className="self-end rounded-xl px-3 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50" onClick={() => setProductForm((current) => ({ ...current, comboComponents: current.comboComponents.filter((_, index) => index !== componentIndex) }))}>Eliminar</button></div><div className="mt-4"><p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">{component.selectionType === 'fijo' ? 'Selecciona un producto' : 'Productos que puede elegir'}</p><div className="grid gap-2">{simpleProducts.map((product) => {
                  const choice = component.products.find((item) => item.productId === product.id);
                  return <div key={product.id} className={`grid gap-3 rounded-xl border p-3 sm:grid-cols-[1fr_150px_120px] ${choice ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200'}`}><label className="flex items-center gap-3 text-sm font-bold"><input type={component.selectionType === 'fijo' ? 'radio' : 'checkbox'} name={component.selectionType === 'fijo' ? `component-${componentIndex}` : undefined} checked={Boolean(choice)} onChange={(event) => selectComboProduct(componentIndex, product.id, event.target.checked)} />{product.name}{!product.active && <Badge>inactivo</Badge>}</label><Field label="Recargo USD"><Input type="number" min="0" step="0.01" disabled={!choice} value={choice?.extraPriceUsd ?? '0'} onChange={(event) => updateComboChoice(componentIndex, product.id, { extraPriceUsd: event.target.value })} /></Field>{component.selectionType === 'eleccion' && <div className="flex items-end pb-2"><Checkbox label="Predeterminado" isDisabled={!choice} isSelected={choice?.default ?? false} onChange={(checked) => setProductForm((current) => ({ ...current, comboComponents: current.comboComponents.map((item, index) => index === componentIndex ? { ...item, products: item.products.map((productChoice) => ({ ...productChoice, default: productChoice.productId === product.id ? checked : checked ? false : productChoice.default })) } : item) }))} /></div>}</div>;
                })}{!simpleProducts.length && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">Crea primero productos simples para poder incluirlos.</p>}</div></div><div className="mt-4"><Checkbox label="Componente activo" isSelected={component.active} onChange={(active) => updateComboComponent(componentIndex, { active })} /></div></div>)}
                {!productForm.comboComponents.length && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">Un combo necesita al menos un componente activo.</p>}
                <div><Button type="button" variant="secondary" onClick={addComboComponent}>+ Agregar componente</Button></div>
              </div>
            </EditorSection>}

            <div className="sticky bottom-0 -mx-1 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-1 pt-4 backdrop-blur"><Button type="button" variant="secondary" onClick={() => setProductOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Guardar producto</Button></div>
          </form>
        )}
      </Modal>
    </div>
  );
}
